import React, { useRef, useState, useEffect } from 'react';
import {
  Tabs,
  Button,
  Space,
  Typography,
  Card,
  Table,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Select,
  Row,
  Col,
  Divider,
  Tooltip,
  App,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  TagsOutlined,
  SearchOutlined,
  ShopOutlined,
  ArrowLeftOutlined,
  InboxOutlined,
  UploadOutlined,
  CameraOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import { useCurrency } from '../hooks/useCurrency';
import { useAppSelector } from '../hooks/redux';
import {
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} from '../services/productsApi';
import {
  useGetProductsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useUpdateProductAvailabilityMutation,
  useDeleteProductMutation,
  useImportProductsMutation,
} from '../services/productsApi';
import CsvImportModal from '../components/common/CsvImportModal';
import MenuIngestModal from '../components/menu-ingest/MenuIngestModal';
import VariantGroupsModal from '../components/menu/VariantGroupsModal';
import {
  useGetProductionCentersQuery,
  useCreateProductionCenterMutation,
  useUpdateProductionCenterMutation,
  useDeleteProductionCenterMutation,
  useGetActiveProductionCentersQuery,
  ProductionCenter,
  CreateProductionCenterDto,
  UpdateProductionCenterDto,
} from '../services/productionCenterApi';
import { useGetPrinterConfigurationsQuery } from '../services/printersApi';
import { useIsModuleEnabled } from '../hooks/useIsModuleEnabled';
import { MODULE_KEYS } from '../constants/moduleKeys';
import { isAdmin, isManager, isChef } from '../utils/roleHelpers';
import ProductStockTab from '../components/menu/ProductStockTab';
import ProductInventorySection from '../components/menu/ProductInventorySection';
import MenuDigitalTab from '../components/menuDigital/MenuDigitalTab';

const { Title } = Typography;
const { TextArea } = Input;

const MenuManagement: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { currencySymbol } = useCurrency();
  // `message` DEBE salir de App.useApp(): el import estático de antd v5 no
  // consume el contexto del ConfigProvider y los avisos de esta pantalla no se
  // mostraban. Convención del proyecto, ver AiSettings y ProductInventorySection.
  const { modal, message } = App.useApp();
  const [activeTab, setActiveTab] = useState('products');
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [isProductionCenterModalOpen, setIsProductionCenterModalOpen] = useState(false);
  const [editingProductionCenter, setEditingProductionCenter] = useState<ProductionCenter | null>(null);
  const [productForm] = Form.useForm();
  const [categoryForm] = Form.useForm();
  const [productionCenterForm] = Form.useForm();

  // Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [showMenuIngest, setShowMenuIngest] = useState(false);

  // Variant groups modal state
  const [variantGroupsProduct, setVariantGroupsProduct] = useState<{ id: string; name: string } | null>(null);
  const [importProducts] = useImportProductsMutation();

  // Search states
  const [productSearch, setProductSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [productionCenterSearch, setProductionCenterSearch] = useState('');

  // Product filter states
  const [productCategoryFilter, setProductCategoryFilter] = useState<string | undefined>(undefined);
  const [productProductionCenterFilter, setProductProductionCenterFilter] = useState<string | undefined>(undefined);

  // Inline category creation in product modal
  const [newProductCategoryName, setNewProductCategoryName] = useState('');
  const productCategoryInputRef = useRef<any>(null);
  // Inline production-center creation in product modal (mismo patrón)
  const [newProductionCenterName, setNewProductionCenterName] = useState('');
  // Acción rápida de la lista: id del producto que se está alternando
  const [togglingAvailability, setTogglingAvailability] = useState<string | null>(null);

  // Inventory/Stock tab — same setting+plan rule the rest of the app uses
  const { user } = useAppSelector((state) => state.auth);
  const { enabled: isInventarioEnabled } = useIsModuleEnabled(MODULE_KEYS.Inventario);
  const canSeeStockTab = isInventarioEnabled && (isAdmin(user?.role) || isManager(user?.role) || isChef(user?.role));
  const { enabled: isMenuDigitalEnabled } = useIsModuleEnabled(MODULE_KEYS.MenuDigital);
  const { enabled: isMenuIngestEnabled } = useIsModuleEnabled(MODULE_KEYS.IngestaMenuIA);

  useEffect(() => {
    if (activeTab === 'stock' && !canSeeStockTab) {
      setActiveTab('products');
    }
  }, [canSeeStockTab, activeTab]);

  // `/menu-management?ingesta=1` abre directo la carga de la carta con IA: es el botón
  // del checklist de primeros pasos. Se espera a que el módulo resuelva (si está
  // apagado, la página queda normal) y se limpia el parámetro para que un refresh o el
  // botón "atrás" no vuelvan a abrir el modal.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('ingesta') !== '1' || !isMenuIngestEnabled) return;
    setShowMenuIngest(true);
    const next = new URLSearchParams(searchParams);
    next.delete('ingesta');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, isMenuIngestEnabled]);

  useEffect(() => {
    if (activeTab === 'menu-digital' && !isMenuDigitalEnabled) {
      setActiveTab('products');
    }
  }, [isMenuDigitalEnabled, activeTab]);

  // Queries - no skip needed, token has restaurantId
  const { data: categories = [], isLoading: loadingCategories } = useGetCategoriesQuery({ activeOnly: false });
  const { data: products = [], isLoading: loadingProducts } = useGetProductsQuery({ availableOnly: false });
  const { data: productionCenters = [], isLoading: loadingProductionCenters } = useGetProductionCentersQuery();
  const { data: activeProductionCenters = [] } = useGetActiveProductionCentersQuery();

  // Normalize text for accent-insensitive search
  const normalizeText = (text: string) =>
    text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  // Filter products based on search, category, and production center (accent-insensitive)
  const filteredProducts = products.filter((product: any) => {
    const searchNormalized = normalizeText(productSearch);
    const matchesSearch = normalizeText(product.name).includes(searchNormalized) ||
      (product.description ? normalizeText(product.description).includes(searchNormalized) : false);
    const matchesCategory = !productCategoryFilter || product.categoryId === productCategoryFilter;
    const matchesPC = !productProductionCenterFilter ||
      product.productionCenterIds?.includes(productProductionCenterFilter);
    return matchesSearch && matchesCategory && matchesPC;
  });

  // Filter categories based on search (accent-insensitive)
  const filteredCategories = categories.filter((category: any) => {
    const searchNormalized = normalizeText(categorySearch);
    return normalizeText(category.name).includes(searchNormalized) ||
      (category.description ? normalizeText(category.description).includes(searchNormalized) : false);
  });

  // Filter production centers based on search (accent-insensitive)
  const filteredProductionCenters = productionCenters.filter((pc: ProductionCenter) => {
    const searchNormalized = normalizeText(productionCenterSearch);
    return normalizeText(pc.name).includes(searchNormalized) ||
      (pc.description ? normalizeText(pc.description).includes(searchNormalized) : false);
  });

  // Mutations
  const [createCategory, { isLoading: creatingCategory }] = useCreateCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();
  const [createProduct] = useCreateProductMutation();
  const [updateProduct] = useUpdateProductMutation();
  const [deleteProduct] = useDeleteProductMutation();
  const [updateProductAvailability] = useUpdateProductAvailabilityMutation();
  const [createProductionCenter, { isLoading: creatingProductionCenter }] = useCreateProductionCenterMutation();
  const [updateProductionCenter] = useUpdateProductionCenterMutation();
  const [deleteProductionCenter] = useDeleteProductionCenterMutation();

  // Impresoras cloud print, para asignar la de comandas desde el modal del centro
  const { data: printerConfigs } = useGetPrinterConfigurationsQuery();

  // Inline category creation from product modal
  const handleCreateProductCategoryInline = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!newProductCategoryName.trim()) return;
    try {
      const result = await createCategory({
        name: newProductCategoryName.trim(),
        isActive: true,
      }).unwrap();
      setNewProductCategoryName('');
      productForm.setFieldsValue({ categoryId: result.id });
      message.success('Categoría creada');
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al crear categoría');
    }
  };

  // Inline production-center creation from product modal. Solo pide el nombre:
  // el resto de la configuración (imprime ticket, muestra en pantalla, orden)
  // tiene defaults sensatos y se ajusta en su propia pestaña. La idea es no
  // sacar a nadie del modal a medio dar de alta un producto.
  const handleCreateProductionCenterInline = async (e: React.MouseEvent) => {
    e.preventDefault();
    const nombre = newProductionCenterName.trim();
    if (!nombre) return;
    try {
      const result = await createProductionCenter({
        name: nombre,
        printTicket: true,
        showOnScreen: true,
      }).unwrap();
      setNewProductionCenterName('');
      // Se agrega a lo ya seleccionado en vez de reemplazarlo: el producto
      // puede prepararse en varios centros.
      const actuales: string[] = productForm.getFieldValue('productionCenterIds') || [];
      productForm.setFieldsValue({ productionCenterIds: [...actuales, result.id] });
      message.success('Centro de producción creado');
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al crear el centro de producción');
    }
  };

  // Acciones rápidas de la lista. El id en curso se guarda para poner el
  // spinner solo en el interruptor tocado y no en toda la columna.
  const handleToggleAvailability = async (product: any, isAvailable: boolean) => {
    setTogglingAvailability(product.id);
    try {
      await updateProductAvailability({ id: product.id, isAvailable }).unwrap();
    } catch {
      message.error('No se pudo cambiar la disponibilidad');
    } finally {
      setTogglingAvailability(null);
    }
  };

  // Product handlers
  const handleAddProduct = () => {
    setEditingProduct(null);
    productForm.resetFields();
    setIsProductModalOpen(true);
  };

  const handleEditProduct = (product: any) => {
    setEditingProduct(product);
    productForm.setFieldsValue({
      ...product,
      productionCenterIds: product.productionCenterIds || [],
    });
    setIsProductModalOpen(true);
  };

  const handleDeleteProduct = async (productId: string) => {
    try {
      await deleteProduct(productId).unwrap();
      message.success('Producto eliminado correctamente');
    } catch (error: any) {
      console.error('Error deleting product:', error);
      const errorMsg = error?.status === 403
        ? 'No tienes permisos para eliminar productos'
        : error?.data?.message || 'Error al eliminar el producto';
      message.error(errorMsg, 5);
    }
  };

  const handleProductModalOk = async () => {
    try {
      const values = await productForm.validateFields();

      if (editingProduct) {
        await updateProduct({
          id: editingProduct.id,
          ...values,
        }).unwrap();
        message.success('Producto actualizado correctamente');
      } else {
        await createProduct(values).unwrap();
        message.success('Producto creado correctamente');
      }

      setIsProductModalOpen(false);
      productForm.resetFields();
    } catch (error: any) {
      console.error('Error saving product:', error);
      let errorMsg = 'Error al guardar el producto';

      if (error?.status === 403) {
        errorMsg = 'No tienes permisos para ' + (editingProduct ? 'editar' : 'crear') + ' productos';
      } else if (error?.status === 400) {
        errorMsg = error?.data?.message || 'Los datos del producto son inválidos';
      } else if (error?.data?.message) {
        errorMsg = error.data.message;
      }

      message.error(errorMsg, 5);
    }
  };

  // Category handlers
  const handleAddCategory = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    setIsCategoryModalOpen(true);
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    categoryForm.setFieldsValue(category);
    setIsCategoryModalOpen(true);
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      await deleteCategory(categoryId).unwrap();
      message.success('Categoría eliminada correctamente');
    } catch (error: any) {
      console.error('Error deleting category:', error);
      const errorMsg = error?.status === 403
        ? 'No tienes permisos para eliminar categorías'
        : error?.data?.message || 'Error al eliminar la categoría';
      message.error(errorMsg, 5);
    }
  };

  const submitCategory = async (values: any) => {
    try {
      if (editingCategory) {
        await updateCategory({
          id: editingCategory.id,
          ...values,
        }).unwrap();
        message.success('Categoría actualizada correctamente');
      } else {
        await createCategory(values).unwrap();
        message.success('Categoría creada correctamente');
      }

      setIsCategoryModalOpen(false);
      categoryForm.resetFields();
    } catch (error: any) {
      console.error('Error saving category:', error);
      let errorMsg = 'Error al guardar la categoría';

      if (error?.status === 403) {
        errorMsg = 'No tienes permisos para ' + (editingCategory ? 'editar' : 'crear') + ' categorías';
      } else if (error?.status === 400) {
        errorMsg = error?.data?.message || 'Los datos de la categoría son inválidos';
      } else if (error?.data?.message) {
        errorMsg = error.data.message;
      }

      message.error(errorMsg, 5);
    }
  };

  const handleCategoryModalOk = async () => {
    let values: any;
    try {
      values = await categoryForm.validateFields();
    } catch {
      return; // validation errors already shown on the form
    }

    // Deactivating hides every product of the category from the POS — warn first
    const deactivating = !!editingCategory && editingCategory.isActive && values.isActive === false;
    if (deactivating) {
      const count = editingCategory.productCount
        ?? products.filter((p: any) => p.categoryId === editingCategory.id).length;
      modal.confirm({
        title: 'Desactivar categoría',
        content: `Al desactivar esta categoría, sus ${count} producto(s) dejarán de mostrarse en el punto de venta. ¿Continuar?`,
        okText: 'Sí, desactivar',
        okType: 'danger',
        cancelText: 'Cancelar',
        onOk: () => submitCategory(values),
      });
      return;
    }

    await submitCategory(values);
  };

  // Production Center handlers
  const handleAddProductionCenter = () => {
    setEditingProductionCenter(null);
    productionCenterForm.resetFields();
    productionCenterForm.setFieldsValue({
      printTicket: true,
      showOnScreen: true,
      isActive: true,
      sortOrder: 0,
    });
    setIsProductionCenterModalOpen(true);
  };

  const handleEditProductionCenter = (pc: ProductionCenter) => {
    setEditingProductionCenter(pc);
    productionCenterForm.setFieldsValue(pc);
    setIsProductionCenterModalOpen(true);
  };

  const handleDeleteProductionCenter = async (id: string) => {
    try {
      await deleteProductionCenter(id).unwrap();
      message.success('Centro de producción eliminado correctamente');
    } catch (error: any) {
      console.error('Error deleting production center:', error);
      const errorMsg = error?.status === 403
        ? 'No tienes permisos para eliminar centros de producción'
        : error?.data?.message || 'Error al eliminar el centro de producción';
      message.error(errorMsg, 5);
    }
  };

  const handleProductionCenterModalOk = async () => {
    try {
      const values = await productionCenterForm.validateFields();

      if (editingProductionCenter) {
        await updateProductionCenter({
          id: editingProductionCenter.id,
          data: values as UpdateProductionCenterDto,
        }).unwrap();
        message.success('Centro de producción actualizado correctamente');
      } else {
        await createProductionCenter(values as CreateProductionCenterDto).unwrap();
        message.success('Centro de producción creado correctamente');
      }

      setIsProductionCenterModalOpen(false);
      productionCenterForm.resetFields();
    } catch (error: any) {
      console.error('Error saving production center:', error);
      let errorMsg = 'Error al guardar el centro de producción';

      if (error?.status === 403) {
        errorMsg = 'No tienes permisos para ' + (editingProductionCenter ? 'editar' : 'crear') + ' centros de producción';
      } else if (error?.status === 400) {
        errorMsg = error?.data?.message || 'Los datos del centro de producción son inválidos';
      } else if (error?.data?.message) {
        errorMsg = error.data.message;
      }

      message.error(errorMsg, 5);
    }
  };

  // Product columns
  const productColumns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: any, b: any) => a.name.localeCompare(b.name),
    },
    {
      title: 'Categoría',
      dataIndex: 'categoryId',
      key: 'categoryId',
      render: (categoryId: string) => {
        const category = categories?.find((c) => c.id === categoryId);
        return <Tag color="blue">{category?.name || 'Sin categoría'}</Tag>;
      },
      sorter: (a: any, b: any) => {
        const catA = categories?.find((c) => c.id === a.categoryId)?.name || '';
        const catB = categories?.find((c) => c.id === b.categoryId)?.name || '';
        return catA.localeCompare(catB);
      },
    },
    {
      title: 'Precio',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => `${currencySymbol} ${price.toFixed(2)}`,
      sorter: (a: any, b: any) => a.price - b.price,
    },
    // Disponible es accionable en vez de una mera etiqueta: es el cambio que
    // más se hace a media operación ("se acabó el pollo") y obligaba a abrir el
    // modal completo. Va como columna y NO como otro botón en Acciones, que ya
    // tiene tres.
    //
    // La visibilidad en el menú digital NO tiene interruptor aquí a propósito:
    // se decide en la pestaña Menú Digital, viendo la carta. Un interruptor
    // suelto en una tabla no dice cómo queda el menú, que es justo la pregunta.
    {
      title: 'Disponible',
      dataIndex: 'isAvailable',
      key: 'isAvailable',
      render: (isAvailable: boolean, record: any) => (
        <Tooltip title={isAvailable ? 'Se puede ordenar en el punto de venta' : 'Agotado: no se puede ordenar'}>
          <Switch
            size="small"
            checked={isAvailable}
            loading={togglingAvailability === record.id}
            onChange={(checked) => handleToggleAvailability(record, checked)}
          />
        </Tooltip>
      ),
      sorter: (a: any, b: any) => (a.isAvailable === b.isAvailable ? 0 : a.isAvailable ? -1 : 1),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            type="link"
            icon={<TagsOutlined />}
            onClick={() => setVariantGroupsProduct({ id: record.id, name: record.name })}
            size="small"
          >
            {!isMobile && 'Modificadores'}
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditProduct(record)}
            size="small"
          >
            {!isMobile && 'Editar'}
          </Button>
          {record.hasOrderItems ? (
            <Tooltip title="No se puede eliminar: el producto ya forma parte de órdenes. Desactívalo en su lugar.">
              {/* span wrapper: disabled buttons don't fire the hover events the Tooltip needs */}
              <span>
                <Button type="link" danger icon={<DeleteOutlined />} size="small" disabled>
                  {!isMobile && 'Eliminar'}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Popconfirm
              title="¿Eliminar producto?"
              description="Esta acción no se puede deshacer"
              onConfirm={() => handleDeleteProduct(record.id)}
              okText="Eliminar"
              cancelText="Cancelar"
            >
              <Button type="link" danger icon={<DeleteOutlined />} size="small">
                {!isMobile && 'Eliminar'}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Category columns
  const categoryColumns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: any, b: any) => a.name.localeCompare(b.name),
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
      sorter: (a: any, b: any) => (a.description || '').localeCompare(b.description || ''),
    },
    {
      title: '# Productos',
      dataIndex: 'productCount',
      key: 'productCount',
      render: (count: number) => count ?? 0,
      sorter: (a: any, b: any) => (a.productCount ?? 0) - (b.productCount ?? 0),
    },
    {
      title: 'Estado',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Activa' : 'Inactiva'}
        </Tag>
      ),
      sorter: (a: any, b: any) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditCategory(record)}
            size="small"
          >
            {!isMobile && 'Editar'}
          </Button>
          <Popconfirm
            title="¿Eliminar categoría?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDeleteCategory(record.id)}
            okText="Eliminar"
            cancelText="Cancelar"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              {!isMobile && 'Eliminar'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Production Center columns
  const productionCenterColumns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: ProductionCenter, b: ProductionCenter) => a.name.localeCompare(b.name),
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
      sorter: (a: ProductionCenter, b: ProductionCenter) => (a.description || '').localeCompare(b.description || ''),
    },
    {
      title: 'Imprime Ticket',
      dataIndex: 'printTicket',
      key: 'printTicket',
      render: (printTicket: boolean) => (
        <Tag color={printTicket ? 'blue' : 'default'}>
          {printTicket ? 'Sí' : 'No'}
        </Tag>
      ),
      sorter: (a: ProductionCenter, b: ProductionCenter) => (a.printTicket === b.printTicket ? 0 : a.printTicket ? -1 : 1),
    },
    {
      title: 'Muestra en Pantalla',
      dataIndex: 'showOnScreen',
      key: 'showOnScreen',
      render: (showOnScreen: boolean) => (
        <Tag color={showOnScreen ? 'green' : 'default'}>
          {showOnScreen ? 'Sí' : 'No'}
        </Tag>
      ),
      sorter: (a: ProductionCenter, b: ProductionCenter) => (a.showOnScreen === b.showOnScreen ? 0 : a.showOnScreen ? -1 : 1),
    },
    {
      title: '# Productos',
      dataIndex: 'productCount',
      key: 'productCount',
      render: (count: number) => count || 0,
      sorter: (a: ProductionCenter, b: ProductionCenter) => (a.productCount || 0) - (b.productCount || 0),
    },
    {
      title: 'Estado',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
      sorter: (a: ProductionCenter, b: ProductionCenter) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: ProductionCenter) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditProductionCenter(record)}
            size="small"
          >
            {!isMobile && 'Editar'}
          </Button>
          <Popconfirm
            title="¿Eliminar centro de producción?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDeleteProductionCenter(record.id)}
            okText="Eliminar"
            cancelText="Cancelar"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              {!isMobile && 'Eliminar'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Mobile-optimized product cards
  const renderProductCards = () => (
    <Row gutter={[16, 16]}>
      {filteredProducts.map((product: any) => {
        const category = categories?.find((c) => c.id === product.categoryId);
        return (
          <Col xs={24} sm={12} md={8} lg={6} key={product.id}>
            <Card
              size="small"
              title={product.name}
              extra={
                <Tag color={product.isAvailable ? 'green' : 'red'}>
                  {product.isAvailable ? 'Disponible' : 'No disponible'}
                </Tag>
              }
              actions={[
                <Button
                  type="link"
                  icon={<TagsOutlined />}
                  onClick={() => setVariantGroupsProduct({ id: product.id, name: product.name })}
                  size="small"
                >
                  Modif.
                </Button>,
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => handleEditProduct(product)}
                  size="small"
                >
                  Editar
                </Button>,
                product.hasOrderItems ? (
                  <Tooltip title="No se puede eliminar: el producto ya forma parte de órdenes. Desactívalo en su lugar.">
                    <span>
                      <Button type="link" danger icon={<DeleteOutlined />} size="small" disabled>
                        Eliminar
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Popconfirm
                    title="¿Eliminar producto?"
                    description="Esta acción no se puede deshacer"
                    onConfirm={() => handleDeleteProduct(product.id)}
                    okText="Eliminar"
                    cancelText="Cancelar"
                  >
                    <Button type="link" danger icon={<DeleteOutlined />} size="small">
                      Eliminar
                    </Button>
                  </Popconfirm>
                ),
              ]}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Typography.Text type="secondary">Categoría:</Typography.Text>
                  <br />
                  <Tag color="blue">{category?.name || 'Sin categoría'}</Tag>
                </div>
                <div>
                  <Typography.Text type="secondary">Precio:</Typography.Text>
                  <br />
                  <Typography.Text strong style={{ fontSize: '18px' }}>
                    {currencySymbol} {product.price.toFixed(2)}
                  </Typography.Text>
                </div>
                {product.description && (
                  <div>
                    <Typography.Text type="secondary">Descripción:</Typography.Text>
                    <br />
                    <Typography.Text ellipsis={{ tooltip: product.description }}>
                      {product.description}
                    </Typography.Text>
                  </div>
                )}
                {/* La misma acción rápida de la tabla. Va en el cuerpo y no en
                    `actions`, que ya tiene tres botones y en teléfono se aprieta
                    hasta cortar las etiquetas. */}
                <div style={{ paddingTop: 4 }}>
                  <Space size={6}>
                    <Switch
                      size="small"
                      checked={product.isAvailable}
                      loading={togglingAvailability === product.id}
                      onChange={(checked) => handleToggleAvailability(product, checked)}
                    />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Disponible
                    </Typography.Text>
                  </Space>
                </div>
              </Space>
            </Card>
          </Col>
        );
      })}
    </Row>
  );

  // Mobile-optimized category cards
  const renderCategoryCards = () => (
    <Row gutter={[16, 16]}>
      {filteredCategories.map((category: any) => (
        <Col xs={24} sm={12} md={8} key={category.id}>
          <Card
            size="small"
            title={category.name}
            extra={
              <Tag color={category.isActive ? 'green' : 'red'}>
                {category.isActive ? 'Activa' : 'Inactiva'}
              </Tag>
            }
            actions={[
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditCategory(category)}
                size="small"
              >
                Editar
              </Button>,
              <Popconfirm
                title="¿Eliminar categoría?"
                description="Esta acción no se puede deshacer"
                onConfirm={() => handleDeleteCategory(category.id)}
                okText="Eliminar"
                cancelText="Cancelar"
              >
                <Button type="link" danger icon={<DeleteOutlined />} size="small">
                  Eliminar
                </Button>
              </Popconfirm>,
            ]}
          >
            <Space direction="vertical" size={4}>
              {category.description && (
                <Typography.Text type="secondary">{category.description}</Typography.Text>
              )}
              <Tag>{category.productCount ?? 0} productos</Tag>
            </Space>
          </Card>
        </Col>
      ))}
    </Row>
  );

  // Mobile-optimized production center cards
  const renderProductionCenterCards = () => (
    <Row gutter={[16, 16]}>
      {filteredProductionCenters.map((pc: ProductionCenter) => (
        <Col xs={24} sm={12} md={8} key={pc.id}>
          <Card
            size="small"
            title={pc.name}
            extra={
              <Tag color={pc.isActive ? 'green' : 'red'}>
                {pc.isActive ? 'Activo' : 'Inactivo'}
              </Tag>
            }
            actions={[
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditProductionCenter(pc)}
                size="small"
              >
                Editar
              </Button>,
              <Popconfirm
                title="¿Eliminar centro de producción?"
                description="Esta acción no se puede deshacer"
                onConfirm={() => handleDeleteProductionCenter(pc.id)}
                okText="Eliminar"
                cancelText="Cancelar"
              >
                <Button type="link" danger icon={<DeleteOutlined />} size="small">
                  Eliminar
                </Button>
              </Popconfirm>,
            ]}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {pc.description && (
                <Typography.Text type="secondary">{pc.description}</Typography.Text>
              )}
              <Space wrap>
                <Tag color={pc.printTicket ? 'blue' : 'default'}>
                  {pc.printTicket ? 'Imprime' : 'No imprime'}
                </Tag>
                <Tag color={pc.showOnScreen ? 'green' : 'default'}>
                  {pc.showOnScreen ? 'En pantalla' : 'Sin pantalla'}
                </Tag>
                <Tag>{pc.productCount || 0} productos</Tag>
              </Space>
            </Space>
          </Card>
        </Col>
      ))}
    </Row>
  );

  // Build tabs array dynamically based on facturación status
  const tabItems = [
    {
      key: 'products',
      label: (
        <span>
          <AppstoreOutlined />
          {!isMobile && ' Productos'}
        </span>
      ),
      children: (
        <Card>
          <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddProduct}
              size={isMobile ? 'middle' : 'large'}
            >
              Agregar Producto
            </Button>
            {/* Gateado igual que el resto de la pantalla. Sin esto, un tenant que no la tiene
                ve el botón, elige su PDF, espera a que el navegador lo rasterice y recién
                entonces se topa con un 403 — el mismo error que la autocaptura de gastos ya
                había pagado con el gate del módulo apagado.

                El hook usa allowPlanBypass por default, que es exactamente lo que hace el
                backend: el plan Profesional la incluye. */}
            {isMenuIngestEnabled && (
              <Button
                icon={<CameraOutlined />}
                onClick={() => setShowMenuIngest(true)}
                size={isMobile ? 'middle' : 'large'}
              >
                {isMobile ? 'Cargar carta' : 'Cargar mi carta con IA'}
              </Button>
            )}
            <Button
              icon={<UploadOutlined />}
              onClick={() => setShowImportModal(true)}
              size={isMobile ? 'middle' : 'large'}
            >
              {isMobile ? 'Importar' : 'Importar CSV'}
            </Button>
            <Input
              placeholder="Buscar productos..."
              prefix={<SearchOutlined />}
              allowClear
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              style={{ maxWidth: 300, flex: 1 }}
            />
            <Select
              placeholder="Filtrar por categoría"
              allowClear
              value={productCategoryFilter}
              onChange={(value) => setProductCategoryFilter(value)}
              style={isMobile ? { flex: '1 1 100%' } : { minWidth: 150, maxWidth: 200, flex: 1 }}
              options={categories.map((c: any) => ({ label: c.name, value: c.id }))}
            />
            {activeProductionCenters.length > 0 && (
              <Select
                placeholder="Filtrar por centro"
                allowClear
                value={productProductionCenterFilter}
                onChange={(value) => setProductProductionCenterFilter(value)}
                style={isMobile ? { flex: '1 1 100%' } : { minWidth: 150, maxWidth: 200, flex: 1 }}
                options={activeProductionCenters.map((pc: any) => ({ label: pc.name, value: pc.id }))}
              />
            )}
          </div>
          {isMobile ? (
            loadingProducts ? (
              <Card loading={true} />
            ) : (
              renderProductCards()
            )
          ) : (
            <Table
              columns={productColumns}
              dataSource={filteredProducts}
              loading={loadingProducts}
              rowKey="id"
              scroll={{ x: 800 }}
              pagination={{ pageSize: 10 }}
            />
          )}
        </Card>
      ),
    },
    {
      key: 'categories',
      label: (
        <span>
          <TagsOutlined />
          {!isMobile && ' Categorías'}
        </span>
      ),
      children: (
        <Card>
          <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCategory}
              size={isMobile ? 'middle' : 'large'}
            >
              Agregar Categoría
            </Button>
            <Input
              placeholder="Buscar categorías..."
              prefix={<SearchOutlined />}
              allowClear
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              style={{ maxWidth: 300, flex: 1 }}
            />
          </div>
          {isMobile ? (
            loadingCategories ? (
              <Card loading={true} />
            ) : (
              renderCategoryCards()
            )
          ) : (
            <Table
              columns={categoryColumns}
              dataSource={filteredCategories}
              loading={loadingCategories}
              rowKey="id"
              scroll={{ x: 600 }}
              pagination={{ pageSize: 10 }}
            />
          )}
        </Card>
      ),
    },
    {
      key: 'production-centers',
      label: (
        <span>
          <ShopOutlined />
          {!isMobile && ' Centros de Producción'}
        </span>
      ),
      children: (
        <Card>
          <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddProductionCenter}
              size={isMobile ? 'middle' : 'large'}
            >
              Agregar Centro de Producción
            </Button>
            <Input
              placeholder="Buscar centros de producción..."
              prefix={<SearchOutlined />}
              allowClear
              value={productionCenterSearch}
              onChange={(e) => setProductionCenterSearch(e.target.value)}
              style={{ maxWidth: 300, flex: 1 }}
            />
          </div>
          {isMobile ? (
            loadingProductionCenters ? (
              <Card loading={true} />
            ) : (
              renderProductionCenterCards()
            )
          ) : (
            <Table
              columns={productionCenterColumns}
              dataSource={filteredProductionCenters}
              loading={loadingProductionCenters}
              rowKey="id"
              scroll={{ x: 800 }}
              pagination={{ pageSize: 10 }}
            />
          )}
        </Card>
      ),
    },
    ...(canSeeStockTab
      ? [
          {
            key: 'stock',
            label: (
              <span>
                <InboxOutlined />
                {!isMobile && ' Stock'}
              </span>
            ),
            children: <ProductStockTab />,
          },
        ]
      : []),
    ...(isMenuDigitalEnabled
      ? [
          {
            key: 'menu-digital',
            label: (
              <span>
                <QrcodeOutlined />
                {!isMobile && ' Menú Digital'}
              </span>
            ),
            children: <MenuDigitalTab />,
          },
        ]
      : []),
  ];

  return (
    <div>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/configuracion')}
        style={{ padding: 0, marginBottom: 16 }}
      >
        Volver a Configuración
      </Button>
      <Title level={2}>Gestión de Menú</Title>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size={isMobile ? 'small' : 'large'}
      />

      {/* Product Modal */}
      <Modal
        title={editingProduct ? 'Editar Producto' : 'Agregar Producto'}
        open={isProductModalOpen}
        onOk={handleProductModalOk}
        onCancel={() => setIsProductModalOpen(false)}
        width={isMobile ? '90%' : 600}
      >
        <Form form={productForm} layout="vertical">
          <Form.Item
            name="name"
            label="Nombre"
            rules={[{ required: true, message: 'Ingrese el nombre del producto' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="categoryId"
            label="Categoría"
            rules={[{ required: true, message: 'Seleccione una categoría' }]}
          >
            <Select
              placeholder="Seleccione una categoría"
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px' }}>
                    <Input
                      placeholder="Nueva categoría"
                      ref={productCategoryInputRef}
                      value={newProductCategoryName}
                      onChange={(e) => setNewProductCategoryName(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      size="small"
                    />
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={handleCreateProductCategoryInline}
                      loading={creatingCategory}
                      size="small"
                    >
                      Crear
                    </Button>
                  </Space>
                </>
              )}
              options={categories?.map((c) => ({ label: c.name, value: c.id }))}
            />
          </Form.Item>
          <Form.Item name="description" label="Descripción">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="price"
            label={`Precio (${currencySymbol})`}
            rules={[{ required: true, message: 'Ingrese el precio' }]}
          >
            <InputNumber min={0} step={1} style={{ width: '100%' }} />
          </Form.Item>
          {/* Los tres interruptores en línea: apilados ocupaban tres renglones
              del modal para tres sí/no. En móvil bajan a 8 de ancho cada uno,
              que sigue cabiendo sin cortar la etiqueta. */}
          <Row gutter={16}>
            <Col xs={8} sm={8}>
              <Form.Item
                name="isAvailable"
                label="Disponible"
                valuePropName="checked"
                tooltip="Se muestra en el punto de venta para ordenar. Apágalo cuando el producto se agote temporalmente."
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={8} sm={8}>
              <Form.Item
                name="showInDigitalMenu"
                label={isMobile ? 'En el menú' : 'Mostrar en menú digital'}
                valuePropName="checked"
                initialValue={true}
                tooltip="Aparece en la carta pública que ven tus clientes al escanear el QR. Independiente de Disponible: un platillo agotado hoy sigue en la carta."
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={8} sm={8}>
              <Form.Item
                name="isActive"
                label="Activo"
                valuePropName="checked"
                tooltip="Producto vigente en el catálogo. Desactívalo para darlo de baja sin perder su historial de ventas; dejará de aparecer en el punto de venta."
              >
                <Switch defaultChecked />
              </Form.Item>
            </Col>
          </Row>
          {/* Sin el `length > 0` de antes: quien todavía no tiene centros es
              justo quien necesita crear el primero sin salirse del modal. */}
          <Form.Item
            name="productionCenterIds"
            label="Centros de Producción"
            tooltip="Seleccione los centros donde se prepara este producto. Si no selecciona ninguno, el producto aparecerá en todas las pantallas de cocina."
          >
            <Select
              mode="multiple"
              placeholder="Todos los centros"
              allowClear
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px' }}>
                    <Input
                      placeholder="Nuevo centro"
                      value={newProductionCenterName}
                      onChange={(e) => setNewProductionCenterName(e.target.value)}
                      // Sin esto el Select se traga las teclas y no se puede escribir.
                      onKeyDown={(e) => e.stopPropagation()}
                      size="small"
                    />
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={handleCreateProductionCenterInline}
                      loading={creatingProductionCenter}
                      size="small"
                    >
                      Crear
                    </Button>
                  </Space>
                </>
              )}
              options={activeProductionCenters.map((pc) => ({
                label: pc.name,
                value: pc.id,
              }))}
            />
          </Form.Item>
          {canSeeStockTab && (
            <ProductInventorySection
              productId={editingProduct?.id}
              isMobile={isMobile}
            />
          )}
        </Form>
      </Modal>

      {/* Category Modal */}
      <Modal
        title={editingCategory ? 'Editar Categoría' : 'Agregar Categoría'}
        open={isCategoryModalOpen}
        onOk={handleCategoryModalOk}
        onCancel={() => setIsCategoryModalOpen(false)}
        width={isMobile ? '90%' : 600}
      >
        <Form form={categoryForm} layout="vertical">
          <Form.Item
            name="name"
            label="Nombre"
            rules={[{ required: true, message: 'Ingrese el nombre de la categoría' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Descripción">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="isActive" label="Activa" valuePropName="checked">
            <Switch defaultChecked />
          </Form.Item>
        </Form>
      </Modal>

      {/* Production Center Modal */}
      <Modal
        title={editingProductionCenter ? 'Editar Centro de Producción' : 'Agregar Centro de Producción'}
        open={isProductionCenterModalOpen}
        onOk={handleProductionCenterModalOk}
        onCancel={() => setIsProductionCenterModalOpen(false)}
        width={isMobile ? '90%' : 600}
      >
        <Form form={productionCenterForm} layout="vertical">
          <Form.Item
            name="name"
            label="Nombre"
            rules={[{ required: true, message: 'Ingrese el nombre del centro de producción' }]}
          >
            <Input placeholder="Ej: Cocina Caliente, Bar, etc." />
          </Form.Item>
          <Form.Item name="description" label="Descripción">
            <TextArea rows={2} placeholder="Descripción opcional del centro" />
          </Form.Item>
          <Divider>Configuración de Salida</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="printTicket"
                label="Imprimir Ticket"
                valuePropName="checked"
                tooltip="Imprime un ticket de comanda cuando se envían pedidos a este centro"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="showOnScreen"
                label="Mostrar en Pantalla"
                valuePropName="checked"
                tooltip="Los pedidos aparecen en la pantalla de cocina (KDS) de este centro"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          {/* Registrado SIEMPRE (hidden, no condicional): si el campo no está en el
              form, validateFields() lo omite y el PUT borraba la impresora asignada
              cada vez que se editaba el centro (p.ej. apagar/encender Imprimir Ticket). */}
          <Form.Item
            name="printerConfigurationId"
            label="Impresora de comandas"
            hidden={!printerConfigs?.length}
            tooltip="A qué impresora de cloud print salen las comandas de este centro. Sin selección, salen por la impresora predeterminada."
          >
            <Select
              allowClear
              placeholder="Impresora predeterminada"
              options={(printerConfigs ?? []).map((p) => ({ label: p.name, value: p.id }))}
            />
          </Form.Item>
          <Divider />
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sortOrder" label="Orden">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isActive" label="Activo" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Cargar la carta de papel con IA. Vive en su propio componente: esta pantalla ya es
          larga y la ingesta no comparte nada con el CRUD de productos salvo el refresco. */}
      {/* Sin onImported: confirmar invalida las etiquetas Product y Category, así que las dos
          consultas de esta pantalla se recargan solas. */}
      <MenuIngestModal open={showMenuIngest} onClose={() => setShowMenuIngest(false)} />

      {/* Import CSV Modal */}
      <CsvImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Importar Productos"
        templateEndpoint="/products/import/template"
        templateFileName="plantilla_productos.csv"
        expectedColumns={[
          { name: 'Nombre', label: 'Nombre', required: true, type: 'string' },
          { name: 'Descripcion', label: 'Descripción', required: false, type: 'string' },
          { name: 'Precio', label: 'Precio', required: true, type: 'number' },
          { name: 'Categoria', label: 'Categoría', required: true, type: 'string' },
          { name: 'Activo', label: 'Activo', required: false, type: 'boolean' },
          { name: 'Disponible', label: 'Disponible', required: false, type: 'boolean' },
          { name: 'TiempoPreparacion', label: 'Tiempo Prep.', required: false, type: 'number' },
        ]}
        onImport={async (formData) => {
          const result = await importProducts(formData).unwrap();
          return result;
        }}
      />
      {/* Variant Groups Modal */}
      {variantGroupsProduct && (
        <VariantGroupsModal
          visible={!!variantGroupsProduct}
          onCancel={() => setVariantGroupsProduct(null)}
          productId={variantGroupsProduct.id}
          productName={variantGroupsProduct.name}
        />
      )}
    </div>
  );
};

export default MenuManagement;
